import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import type { AssignableRankDto, RankCode } from '@/api/rank-admin-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import {
  DEFAULT_RANKING_SORT,
  topNSubtitle,
  type RankingSort,
  type RankingSortColumn,
  type RankingViewRow,
  type TopNSetting,
} from '@/lib/analysis-fusion';
import {
  ASSIGNABLE_RANKS,
  RANK_COUNTER_LABELS,
  RANK_COUNTER_ORDER,
  RANK_ORDER,
  RANK_PRESENTATION,
  formatEurOrDash,
  formatPercentileOrDash,
  formatSignedPctOrDash,
  seniorityLabel,
  type RankCounterKey,
} from '@/lib/rank-labels';
import {
  useRankingColumnWidths,
  type ColumnWidths,
} from '@/routes/admin/campaigns/analysis-column-widths';
import { cn } from '@/lib/utils';

// =============================================================================
// analysis-ranking-card.tsx — Card « Ranking nominatif » de la page Analyse fusionnée (lot U1/U1-bis).
// =============================================================================
// ADR-FUSION-ANALYSE-RANG-01 : le tableau de ranking ET le tableau de travail du RANG sont désormais UN
// SEUL tableau (DBU §2.6 v2.8). Une ligne par Profil × Séniorité × Fournisseur ; TOUTES les colonnes de
// la décision 2 sont présentes — aucun masquage de colonnes.
//
// CORRECTIFS U1-bis (retours GATE_HUMAN du 2026-09-18, verbatim) :
//   • « il faut ajouter une colonne avec le nom de la sous-famille. Cette colonne se positionne comme
//     première colonne. » ⇒ colonne « Sous-famille » en TÊTE, triable, présente à l'export ;
//   • « La largeur des colonnes doit être modificable par l'utilisateur. » + réponse « 2. OUi »
//     (mémorisée entre visites) ⇒ poignée de redimensionnement par en-tête (souris ET clavier), largeur
//     persistée par `analysis-column-widths.ts` (préférence d'AFFICHAGE, G0-S14-5) ;
//   • « Une checkbox doit être présente pour sélectionner toutes les lignes affichées » ⇒ case de
//     tête à trois états (aucune / partielle / toutes), qui alimente l'attribution de rang EN MASSE ;
//   • « le TOP N est toujours appliqué, or cela ne devrait pas être le cas » ⇒ le sous-titre dit la
//     vérité : « toutes les lignes de chaque couple » tant que le réglage est inactif.
//
// DENSITÉ (retour « la page ne se redimensionne pas correctement », puis retour du 2026-09-18 plaçant
// la colonne des indicateurs À DROITE du ranking) : `table-fixed` + <colgroup> de largeurs par défaut
// dont la SOMME (836 px depuis le retrait de la colonne « Négo. », voir ci-dessous) tient dans la
// colonne principale d'un écran large (≈ 1600 px de viewport :
// 1600 − 240 px de rail − 56 px de gouttière − 340 px de colonne droite − 24 px de gouttière − 48 px de
// marge de card ≈ 890 px). Sur un écran plus étroit, le conteneur DÉFILE horizontalement plutôt que de
// comprimer les colonnes. La table porte `min-w-full` ET
// une largeur EXPLICITE égale à la somme des colonnes : en dessous de la largeur disponible, elle
// s'étire pour remplir (aucun blanc à droite) ; au-dessus — donc uniquement quand l'utilisateur a
// LUI-MÊME élargi une colonne — elle déborde et son conteneur défile. Les cellules de texte libre sont
// TRONQUÉES avec un `title` (jamais un empilement sur quatre lignes).
//
// Ce qui est CONSERVÉ de l'ex-écran « Référencement / Rang » (décisions 7 et 8) : tag unitaire, tag en
// masse, chips filtrantes dédupliquées par binôme. SUPPRIMÉS : les 4 curseurs de présélection et la
// fourchette de TJM (décision 11).
//
// K-ANONYMITY (décision 3) : « Écart méd. » et « Percentile » d'un couple sous le seuil affichent « — »
// (title « masqué — K-anonymity »), JAMAIS 0 €. Un tiret d'ABSENCE de réponse porte un title distinct.
//
// WCAG 2.1 AA : <table>/<caption sr-only>/<th scope>, en-têtes de tri <button> + aria-sort, poignées de
// largeur au clavier (flèches), case « tout sélectionner » nommée et à état indéterminé, chips
// <button aria-pressed>, rang DOUBLÉ libellé + icône.
//
// DÉPLACEMENT DU 2026-09-22 (décision GATE_HUMAN, verbatim : « il faut déplacer seulement la
// fonctionnalité négociation sur la page […]/operational-view/fournisseurs ») : la colonne « Négo. »
// (⇄) et son compteur d'en-tête « Ouvertes à la négociation n / N » ONT QUITTÉ cet écran. L'ouverture
// vit désormais dans la vue opérationnelle Fournisseurs, panneau de détail du fournisseur, parce que
// c'est là que l'admin constate qu'un TJM n'est pas compétitif et décide de renégocier. Le compteur
// est parti AVEC la colonne : hors de l'écran qui porte l'action, il annonçait un état que rien ici
// ne permettait plus ni de produire ni d'interpréter. Ce ranking redevient un écran d'ANALYSE pure.

/** Colonne du tableau : id stable (clé de largeur), libellé, clé de tri, alignement, largeur par défaut. */
interface ColumnDef {
  /** Id STABLE — clé de la largeur mémorisée. Ne change jamais (un renommage perdrait la préférence). */
  readonly id: string;
  readonly label: string;
  /**
   * Abréviation AFFICHÉE quand le libellé entier est un mot insécable trop large pour sa colonne
   * (« Percentile »). Le nom ACCESSIBLE reste le libellé entier : l'abréviation est `aria-hidden`,
   * doublée d'un `sr-only`. Sans elle, le mot déborderait sur la colonne voisine.
   */
  readonly shortLabel?: string;
  /** Clé de tri (`null` si la colonne n'est pas triable). */
  readonly sortKey: RankingSortColumn | null;
  readonly numeric?: boolean;
  /** Largeur par défaut (px). La somme des colonnes tient dans la colonne principale d'un écran large. */
  readonly defaultWidth: number;
}

/** Colonne de SÉLECTION (case à cocher) — sans libellé visible, non triable, non redimensionnable. */
const SELECTION_COLUMN_ID = 'selection';
const SELECTION_COLUMN_WIDTH = 28;

/** Les 12 colonnes de données, dans l'ordre d'affichage (Sous-famille EN TÊTE — U1-bis). */
const COLUMNS: readonly ColumnDef[] = [
  { id: 'sousFamille', label: 'Sous-famille', sortKey: 'sousFamille', defaultWidth: 72 },
  { id: 'profil', label: 'Profil', sortKey: 'profil', defaultWidth: 86 },
  { id: 'seniorite', label: 'Séniorité', sortKey: 'seniorite', defaultWidth: 60 },
  { id: 'tjmC1', label: 'TJM C1', sortKey: 'tjmC1', numeric: true, defaultWidth: 48 },
  { id: 'tjmC2', label: 'TJM C2', sortKey: 'tjmC2', numeric: true, defaultWidth: 48 },
  {
    id: 'ecartMediane',
    label: 'Écart méd.',
    sortKey: 'ecartMediane',
    numeric: true,
    defaultWidth: 52,
  },
  {
    id: 'ecartCible',
    label: 'Écart prix cible',
    sortKey: 'ecartCible',
    numeric: true,
    defaultWidth: 56,
  },
  { id: 'fournisseur', label: 'Fournisseur', sortKey: 'fournisseur', defaultWidth: 88 },
  { id: 'siret', label: 'SIRET', sortKey: 'siret', defaultWidth: 78 },
  { id: 'rang', label: 'Rang', sortKey: 'rang', defaultWidth: 74 },
  {
    id: 'percentile',
    label: 'Percentile',
    shortLabel: 'Perc.',
    sortKey: 'percentile',
    numeric: true,
    defaultWidth: 56,
  },
  { id: 'tag', label: 'Tag', sortKey: null, defaultWidth: 90 },
];

/** Ids connus du stockage des largeurs (borne les entrées reportées — valeurs inconnues ignorées). */
const COLUMN_IDS: readonly string[] = COLUMNS.map((c) => c.id);

/** Pas de redimensionnement au CLAVIER (px par flèche) — accessible sans souris (WCAG 2.1.1). */
const KEYBOARD_RESIZE_STEP = 16;

const CELL = 'px-1.5 py-2 align-top text-xs';
const NUM_CELL = `${CELL} whitespace-nowrap text-right tabular-nums`;
/** Cellule de texte libre : une seule ligne, coupée proprement, valeur entière portée par le `title`. */
const TEXT_CELL = `${CELL} truncate`;
/** En-tête : police resserrée (la densité prime), aligné en bas, `relative` pour la poignée. */
// `break-words` : un libellé d'un SEUL mot (« Percentile ») doit se couper dans sa colonne plutôt que
// de déborder sur la suivante (constaté le 2026-09-18 après le passage du ranking en colonne étroite).
const HEAD_CELL =
  'relative px-1.5 py-2 align-bottom text-[10px] tracking-normal break-words';

export interface AnalysisRankingCardProps {
  /** Lignes AFFICHÉES (filtres + TOP N + tri déjà appliqués par la page). */
  readonly rows: readonly RankingViewRow[];
  /** Nombre de lignes chargées AVANT filtres (distingue « aucune analyse » de « aucun résultat »). */
  readonly totalRows: number;
  /** Nombre de binômes affichés (unité du tag de rang). */
  readonly coupleCount: number;
  /** Nombre de fournisseurs DISTINCTS affichés. */
  readonly supplierCount: number;
  /** Réglage TOP N courant (`'tous'` = inactif) — affiché tel quel dans l'en-tête de la card. */
  readonly topN: TopNSetting;
  /** Compteurs par rang, dédupliqués par binôme (chips filtrantes). */
  readonly counters: Record<RankCounterKey, number>;
  /** Chip de tag active (état d'URL) ; `undefined` = aucun filtre de tag. */
  readonly tag: RankCode | undefined;
  readonly onToggleTag: (tag: RankCode) => void;
  /** Tri courant (état d'URL). */
  readonly sort: RankingSort;
  readonly onSort: (column: RankingSortColumn) => void;
  /** Libellé de profil (catalogue figé) ; absent ⇒ seul le code est rendu. */
  readonly profileLabels: ReadonlyMap<string, string>;
  /** Sélection EN MASSE (clés de binômes) — état in-memory de session, jamais localStorage. */
  readonly selected: ReadonlySet<string>;
  readonly onToggleSelected: (coupleKey: string) => void;
  /** Coche TOUS les binômes des lignes AFFICHÉES (case de tête — U1-bis). */
  readonly onSelectAllDisplayed: (coupleKeys: readonly string[]) => void;
  readonly onClearSelection: () => void;
  readonly onApplyMassRank: (rank: AssignableRankDto) => void;
  readonly onRemoveMassRank: () => void;
  /** Tag UNITAIRE : pose ou retrait sur le binôme de la ligne. */
  readonly onChangeRank: (coupleKey: string, rank: RankCode) => void;
  /** `true` pendant une mutation de tag (les combos sont alors désactivés). */
  readonly tagPending: boolean;
  /** Message d'erreur d'une commande de tag, rendu role="alert". */
  readonly errorMessage: string | undefined;
  readonly isPending: boolean;
  readonly loadErrorMessage: string | undefined;
}

export function AnalysisRankingCard({
  rows,
  totalRows,
  coupleCount,
  supplierCount,
  topN,
  counters,
  tag,
  onToggleTag,
  sort,
  onSort,
  profileLabels,
  selected,
  onToggleSelected,
  onSelectAllDisplayed,
  onClearSelection,
  onApplyMassRank,
  onRemoveMassRank,
  onChangeRank,
  tagPending,
  errorMessage,
  isPending,
  loadErrorMessage,
}: AnalysisRankingCardProps): React.JSX.Element {
  // Largeurs MÉMORISÉES entre visites (préférence d'affichage isolée — G0-S14-5, RULE_REF_006).
  const [widths, setColumnWidth, resetColumnWidths] = useRankingColumnWidths(COLUMN_IDS);
  const hasCustomWidths = Object.keys(widths).length > 0;
  /** Largeur TOTALE de la table : somme des colonnes effectives (voir la note de densité en tête). */
  const tableWidth =
    SELECTION_COLUMN_WIDTH +
    COLUMNS.reduce((total, column) => total + columnWidth(widths, column), 0);

  const sortLabel =
    sort.column === DEFAULT_RANKING_SORT.column
      ? 'profil, puis TJM C2 croissant'
      : `${sortColumnLabel(sort.column)} ${sort.direction === 'asc' ? 'croissant' : 'décroissant'}`;

  // Binômes des lignes AFFICHÉES : cible de la case « tout sélectionner » (jamais les lignes filtrées
  // hors écran — la case coche exactement ce que l'utilisateur voit).
  const displayedCoupleKeys = React.useMemo(
    () => [...new Set(rows.map((r) => r.coupleKey))],
    [rows],
  );

  return (
    <Panel
      headingId="analysis-ranking-heading"
      title="Ranking nominatif"
      aside={
        <span>
          Tri : {sortLabel} · {topNSubtitle(topN)}
        </span>
      }
    >
      <p className="px-6 pt-4 text-xs text-muted-foreground">
        {String(rows.length)} ligne{rows.length > 1 ? 's' : ''} ·{' '}
        {String(supplierCount)} fournisseur{supplierCount > 1 ? 's' : ''} ·{' '}
        {String(coupleCount)} binôme{coupleCount > 1 ? 's' : ''} fournisseur × profil ·
        vue admin, aucune anonymisation
      </p>

      {/* Chips filtrantes (compteurs de tags dédupliqués par binôme). */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 py-3">
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label="Filtrer par tag de rang"
        >
          <span className="text-xs font-medium text-muted-foreground">Tag :</span>
          {RANK_COUNTER_ORDER.map((key) => {
            const rank = counterRank(key);
            const active = tag === rank;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => onToggleTag(rank)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-foreground bg-foreground font-semibold text-background'
                    : 'border-border text-foreground hover:bg-muted',
                )}
              >
                {RANK_COUNTER_LABELS[key]}{' '}
                <span className="font-mono tabular-nums">{String(counters[key])}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Barre d'actions en masse : n'apparaît qu'avec une sélection (densité de l'écran, ADR §5 R-1). */}
      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-y border-border bg-muted/40 px-6 py-3">
          <p
            data-testid="mass-selection-count"
            role="status"
            className="text-xs font-medium"
          >
            {String(selected.size)} binôme{selected.size > 1 ? 's' : ''} sélectionné
            {selected.size > 1 ? 's' : ''}
          </p>
          {ASSIGNABLE_RANKS.map((rank) => (
            <Button
              key={rank}
              type="button"
              variant="outline"
              size="sm"
              disabled={tagPending}
              onClick={() => onApplyMassRank(rank)}
            >
              Appliquer {RANK_PRESENTATION[rank].label}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={tagPending}
            onClick={onRemoveMassRank}
          >
            Retirer le rang
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClearSelection}>
            Effacer la sélection
          </Button>
        </div>
      ) : null}

      {errorMessage !== undefined ? (
        <p role="alert" className="px-6 py-3 text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}

      {isPending ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Chargement du ranking…
        </p>
      ) : loadErrorMessage !== undefined ? (
        <p role="alert" className="px-6 py-4 text-sm text-destructive">
          Erreur de chargement : {loadErrorMessage}
        </p>
      ) : rows.length === 0 ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          {totalRows === 0
            ? 'Aucune analyse consolidée disponible.'
            : 'Aucune ligne pour le filtre courant.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="ds-table min-w-full table-fixed border-collapse"
            style={{ width: `${String(tableWidth)}px` }}
          >
            <caption className="sr-only">
              Ranking nominatif : une ligne par profil, séniorité et fournisseur — sous-famille,
              profil, séniorité, TJM Cycle 1, TJM Cycle 2, écart à la médiane winsorisée, écart au
              prix cible, fournisseur, SIRET, rang éditable, percentile, tag et ouverture de la
              négociation. Un tiret signale une donnée masquée par la K-anonymity ou une absence
              de réponse, jamais un montant nul. La largeur de chaque colonne est ajustable et
              mémorisée.
            </caption>
            {/* Les largeurs vivent dans le <colgroup> : une seule source, lue par table-fixed. */}
            <colgroup>
              <col
                data-testid={`ranking-col-${SELECTION_COLUMN_ID}`}
                style={{ width: `${String(SELECTION_COLUMN_WIDTH)}px` }}
              />
              {COLUMNS.map((column) => (
                <col
                  key={column.id}
                  data-testid={`ranking-col-${column.id}`}
                  style={{ width: `${String(columnWidth(widths, column))}px` }}
                />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={cn(HEAD_CELL, 'text-left')}>
                  <SelectAllCheckbox
                    displayedCoupleKeys={displayedCoupleKeys}
                    selected={selected}
                    onSelectAllDisplayed={onSelectAllDisplayed}
                    onClearSelection={onClearSelection}
                  />
                </th>
                {COLUMNS.map((column) => (
                  <ColumnHeader
                    key={column.id}
                    column={column}
                    sort={sort}
                    onSort={onSort}
                    width={columnWidth(widths, column)}
                    onResize={setColumnWidth}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((view) => (
                <RankingRow
                  key={`${view.coupleKey}::${view.row.seniorityLevel}`}
                  view={view}
                  profileLabel={profileLabels.get(view.row.profileCode)}
                  checked={selected.has(view.coupleKey)}
                  onToggleSelected={onToggleSelected}
                  onChangeRank={onChangeRank}
                  tagPending={tagPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-1 border-t border-border px-6 py-4 text-xs text-muted-foreground">
        <p>
          Écarts calculés sur le TJM C2 (C1 si C2 absent). Écart médiane « — » tant que le couple
          n’atteint pas le nombre de répondants requis par la K-anonymity.
        </p>
        <p className="flex flex-wrap items-center gap-2">
          <span>
            Largeur des colonnes : faites glisser la poignée d’un en-tête, ou utilisez les flèches
            gauche et droite au clavier. Le réglage est mémorisé sur ce poste.
          </span>
          {hasCustomWidths ? (
            <Button type="button" variant="ghost" size="sm" onClick={resetColumnWidths}>
              Rétablir les largeurs par défaut
            </Button>
          ) : null}
        </p>
      </div>
    </Panel>
  );
}

/** Largeur effective d'une colonne : celle mémorisée par l'utilisateur, sinon celle par défaut. */
function columnWidth(widths: ColumnWidths, column: ColumnDef): number {
  return widths[column.id] ?? column.defaultWidth;
}

/** Rang correspondant à une catégorie de compteur (les chips filtrent sur le rang du binôme). */
function counterRank(key: RankCounterKey): RankCode {
  return key === 'nonRetenus' ? 'NonReferenced' : key;
}

/** Libellé d'une colonne de tri (mention « Tri : … » de l'en-tête de card). */
function sortColumnLabel(column: RankingSortColumn): string {
  return COLUMNS.find((c) => c.sortKey === column)?.label ?? column;
}

/**
 * Case « tout sélectionner » de l'en-tête (retour GATE_HUMAN 2026-09-18) : coche TOUS les binômes des
 * lignes AFFICHÉES, décoche tout si la sélection est déjà complète, et porte l'état INDÉTERMINÉ quand
 * la sélection est partielle (`indeterminate` n'est pas un attribut HTML : il se pose sur le nœud).
 * Le nom accessible dit le nombre exact de binômes concernés — la case n'est jamais muette.
 */
function SelectAllCheckbox({
  displayedCoupleKeys,
  selected,
  onSelectAllDisplayed,
  onClearSelection,
}: {
  readonly displayedCoupleKeys: readonly string[];
  readonly selected: ReadonlySet<string>;
  readonly onSelectAllDisplayed: (coupleKeys: readonly string[]) => void;
  readonly onClearSelection: () => void;
}): React.JSX.Element {
  const ref = React.useRef<HTMLInputElement | null>(null);
  const total = displayedCoupleKeys.length;
  const selectedCount = displayedCoupleKeys.filter((key) => selected.has(key)).length;
  const allSelected = total > 0 && selectedCount === total;
  const partial = selectedCount > 0 && selectedCount < total;

  React.useEffect(() => {
    if (ref.current !== null) {
      ref.current.indeterminate = partial;
    }
  }, [partial]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="size-4 rounded border-input"
      checked={allSelected}
      onChange={() => {
        if (allSelected) {
          onClearSelection();
          return;
        }
        onSelectAllDisplayed(displayedCoupleKeys);
      }}
      aria-label={`Sélectionner les ${String(total)} binômes affichés`}
    />
  );
}

/**
 * Libellé d'en-tête. Quand la colonne porte une abréviation, c'est ELLE qui est peinte (`aria-hidden`)
 * et le libellé entier reste le nom ACCESSIBLE (`sr-only`) : l'écran gagne de la place sans qu'un
 * lecteur d'écran n'y perde un mot.
 */
function ColumnLabel({ column }: { readonly column: ColumnDef }): React.JSX.Element {
  if (column.shortLabel === undefined) {
    return <span>{column.label}</span>;
  }
  return (
    <>
      <span aria-hidden="true" title={column.label}>
        {column.shortLabel}
      </span>
      <span className="sr-only">{column.label}</span>
    </>
  );
}

/**
 * En-tête de colonne : bouton de tri + aria-sort (ou en-tête simple si la colonne n'est pas triable),
 * suivi de la POIGNÉE de largeur. Le libellé peut passer à la ligne : il n'est jamais tronqué.
 */
function ColumnHeader({
  column,
  sort,
  onSort,
  width,
  onResize,
}: {
  readonly column: ColumnDef;
  readonly sort: RankingSort;
  readonly onSort: (column: RankingSortColumn) => void;
  readonly width: number;
  readonly onResize: (columnId: string, width: number) => void;
}): React.JSX.Element {
  const align = column.numeric === true ? 'text-right' : 'text-left';
  const key = column.sortKey;
  const active = key !== null && sort.column === key;
  const ariaSort: React.AriaAttributes['aria-sort'] =
    key === null ? undefined : active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
  const Icon = !active ? ChevronsUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <th scope="col" aria-sort={ariaSort} className={cn(HEAD_CELL, align)}>
      {key === null ? (
        <ColumnLabel column={column} />
      ) : (
        <button
          type="button"
          onClick={() => onSort(key)}
          className={cn(
            'inline-flex max-w-full items-start gap-1 rounded-sm text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            column.numeric === true && 'flex-row-reverse text-right',
          )}
        >
          <ColumnLabel column={column} />
          <Icon aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
        </button>
      )}
      <ColumnResizer column={column} width={width} onResize={onResize} />
    </th>
  );
}

/**
 * Poignée de redimensionnement d'une colonne. Souris / tactile : `pointerdown` puis suivi du pointeur
 * (la largeur de DÉPART est la largeur RENDUE de l'en-tête, pour un glissement 1:1). Clavier : flèches
 * gauche/droite par pas de 16 px, Origine/Fin pour les bornes — l'ajustement n'exige donc JAMAIS de
 * souris (WCAG 2.1.1). Le bouton n'a pas de texte visible : son nom accessible porte l'information.
 */
function ColumnResizer({
  column,
  width,
  onResize,
}: {
  readonly column: ColumnDef;
  readonly width: number;
  readonly onResize: (columnId: string, width: number) => void;
}): React.JSX.Element {
  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    const header = event.currentTarget.closest('th');
    const startX = event.clientX;
    const startWidth = header?.getBoundingClientRect().width ?? width;
    const onMove = (move: PointerEvent): void => {
      onResize(column.id, startWidth + (move.clientX - startX));
    };
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onResize(column.id, width - KEYBOARD_RESIZE_STEP);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onResize(column.id, width + KEYBOARD_RESIZE_STEP);
    }
  };

  return (
    <button
      type="button"
      data-testid={`ranking-resize-${column.id}`}
      aria-label={`Ajuster la largeur de la colonne ${column.label} (flèches gauche et droite)`}
      title={`Ajuster la largeur de la colonne ${column.label}`}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className="absolute inset-y-0 right-0 w-1.5 cursor-col-resize rounded-sm hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

interface RankingRowProps {
  readonly view: RankingViewRow;
  readonly profileLabel: string | undefined;
  readonly checked: boolean;
  readonly onToggleSelected: (coupleKey: string) => void;
  readonly onChangeRank: (coupleKey: string, rank: RankCode) => void;
  readonly tagPending: boolean;
}

function RankingRow({
  view,
  profileLabel,
  checked,
  onToggleSelected,
  onChangeRank,
  tagPending,
}: RankingRowProps): React.JSX.Element {
  const row = view.row;
  const supplier = row.raisonSociale === '' ? '—' : row.raisonSociale;
  const levelLabel = seniorityLabel(row.seniorityLevel);
  const rowId = `${view.coupleKey}::${row.seniorityLevel}`;
  // Profil : code ET libellé sur UNE seule ligne, tronquée, la chaîne entière restant lisible au
  // survol (retour GATE_HUMAN : le libellé ne doit plus s'empiler sur quatre lignes).
  const profileTitle =
    profileLabel === undefined ? row.profileCode : `${row.profileCode} · ${profileLabel}`;

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-1.5 py-2 align-top">
        <input
          id={`select-${rowId}`}
          type="checkbox"
          className="size-4 rounded border-input"
          checked={checked}
          onChange={() => onToggleSelected(view.coupleKey)}
          aria-label={`Sélectionner ${supplier} — ${row.profileCode} (${levelLabel})`}
        />
      </td>
      {/* PREMIÈRE colonne : sous-famille du profil (U1-bis) ; « — » quand le référentiel ne la porte pas. */}
      <td className={TEXT_CELL} title={view.sousFamille ?? 'sous-famille inconnue'}>
        {view.sousFamille ?? (
          <>
            <span aria-hidden="true">—</span>
            <span className="sr-only">sous-famille inconnue</span>
          </>
        )}
      </td>
      <th scope="row" className={`${TEXT_CELL} text-left font-medium`} title={profileTitle}>
        <span>{row.profileCode}</span>
        {profileLabel !== undefined ? (
          <span className="ml-1 font-normal text-muted-foreground">{profileLabel}</span>
        ) : null}
      </th>
      <td className={TEXT_CELL} title={levelLabel}>
        {levelLabel}
      </td>
      <td className={NUM_CELL}>{formatEurOrDash(row.responseC1Eur ?? null)}</td>
      <td className={NUM_CELL}>{formatEurOrDash(row.responseC2Eur ?? null)}</td>
      <td className={NUM_CELL}>
        <MetricCell
          kind={view.ecartMedianeKind}
          text={formatSignedPctOrDash(row.ecartP50Pct)}
        />
      </td>
      <td className={NUM_CELL}>
        <MetricCell
          kind={view.ecartCibleKind}
          text={formatSignedPctOrDash(row.ecartTargetPricePct ?? null)}
        />
      </td>
      <td className={`${TEXT_CELL} font-medium`} title={supplier}>
        {supplier}
      </td>
      <td className={`${CELL} truncate font-mono text-[10px]`} title={row.supplierSiret}>
        {row.supplierSiret}
      </td>
      <td className="px-1.5 py-2 align-top">
        {/* Tag UNITAIRE au grain BINÔME : le combo est répété sur chaque ligne-niveau du binôme et
            porte la MÊME valeur (le rang est dénormalisé côté API sur tous les niveaux). */}
        <label htmlFor={`couple-rank-${rowId}`} className="sr-only">
          Rang de {supplier} — {row.profileCode} ({levelLabel})
        </label>
        <select
          id={`couple-rank-${rowId}`}
          className="h-8 w-full rounded-md border border-input bg-transparent px-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          value={row.rank}
          disabled={tagPending}
          onChange={(event) =>
            onChangeRank(view.coupleKey, event.target.value as RankCode)
          }
        >
          {RANK_ORDER.map((rank) => (
            <option key={rank} value={rank}>
              {RANK_PRESENTATION[rank].label}
            </option>
          ))}
        </select>
      </td>
      <td className={NUM_CELL}>
        <MetricCell
          kind={view.percentileKind}
          text={formatPercentileOrDash(row.percentile)}
        />
      </td>
      <td className="px-1.5 py-2 align-top">
        <RankBadge rank={row.rank} />
      </td>
    </tr>
  );
}

/**
 * Cellule métrique : la valeur, ou un tiret QUALIFIÉ. Deux tirets, deux causes, deux libellés — un
 * masquage K-anonymity n'est pas une absence de réponse, et aucun des deux n'est « 0 € » (décision 3).
 */
function MetricCell({
  kind,
  text,
}: {
  readonly kind: 'masked' | 'absent' | 'value';
  readonly text: string;
}): React.JSX.Element {
  if (kind === 'value') {
    return <span>{text}</span>;
  }
  const reason =
    kind === 'masked'
      ? 'masqué — seuil de K-anonymity non atteint sur ce couple'
      : 'aucune réponse sur ce niveau';
  return (
    <span title={reason}>
      <span aria-hidden="true">—</span>
      <span className="sr-only">{reason}</span>
    </span>
  );
}

/** Badge de rang : libellé + icône (la couleur n'est qu'un renfort — ST-NFR-03 / P-06). */
function RankBadge({ rank }: { readonly rank: RankCode }): React.JSX.Element {
  const { label, Icon, badgeClassName, description } = RANK_PRESENTATION[rank];
  return (
    <Badge
      variant="outline"
      // `max-w-full` : sans lui, le badge inline-flex s'étend HORS de sa colonne (table-fixed ne le
      // coupe pas) et vient recouvrir la colonne voisine. Avec lui, le libellé tronque proprement.
      className={cn('max-w-full gap-1 whitespace-nowrap px-1 py-0 text-[10px]', badgeClassName)}
      title={description}
    >
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className="truncate">{label}</span>
    </Badge>
  );
}
